/* YNS Reviews Page — interactive behavior.
   - Stat counters: animated count-up on scroll-in
   - <yns-reviews-all>: fetch a static JSON of reviews, render YNS sticker
     cards in chunks (first chunk animated, rest silent via rAF), filter by
     rating, sort by date/rating, smart per-card product linking
   - Sticky CTA: slide-up after scroll engagement, dismissible
*/

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // -----------------------------------------------------------
  // Count-up — animates a number from 0 to its data-target
  // -----------------------------------------------------------
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function animateCountUp(node) {
    var raw = node.getAttribute('data-target') || '';
    var match = raw.match(/^([\d.,]+)([^\d]*)$/);
    if (!match) return;

    var numeric = parseFloat(match[1].replace(/,/g, ''));
    var suffix = match[2] || '';
    if (isNaN(numeric)) return;

    if (prefersReducedMotion) {
      node.textContent = formatNumber(numeric, raw) + suffix;
      return;
    }

    var duration = 1800;
    var start = performance.now();
    var hasDecimal = match[1].indexOf('.') !== -1;
    var hasComma = match[1].indexOf(',') !== -1;

    function frame(now) {
      var elapsed = now - start;
      var t = Math.min(elapsed / duration, 1);
      var eased = easeOutCubic(t);
      var current = numeric * eased;
      node.textContent = formatNumber(current, raw, hasDecimal, hasComma) + suffix;
      if (t < 1) requestAnimationFrame(frame);
      else node.textContent = formatNumber(numeric, raw, hasDecimal, hasComma) + suffix;
    }
    requestAnimationFrame(frame);
  }

  function formatNumber(value, raw, hasDecimal, hasComma) {
    if (hasDecimal) return value.toFixed(1);
    var rounded = Math.round(value);
    if (hasComma || rounded >= 1000) return rounded.toLocaleString('en-US');
    return String(rounded);
  }

  function initStatHero(root) {
    var stats = root.querySelectorAll('[data-yns-stat]');
    if (!stats.length) return;
    stats.forEach(function (stat, i) { stat.style.setProperty('--yns-anim-index', i); });

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var stat = entry.target;
        observer.unobserve(stat);
        stat.setAttribute('data-anim', 'enter');
        var valueNode = stat.querySelector('[data-target]');
        if (valueNode) setTimeout(function () { animateCountUp(valueNode); }, 200);
      });
    }, { threshold: 0.4, rootMargin: '0px 0px -10% 0px' });

    stats.forEach(function (stat) { observer.observe(stat); });
  }

  // -----------------------------------------------------------
  // Stable string hash — used to pick a product per review
  // deterministically (same reviewer always gets the same product).
  // -----------------------------------------------------------
  function stableHash(str) {
    var h = 5381;
    for (var i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    return Math.abs(h);
  }

  // -----------------------------------------------------------
  // <yns-reviews-all> — fetch static JSON + chunked render
  // -----------------------------------------------------------
  if (!customElements.get('yns-reviews-all')) {
    customElements.define('yns-reviews-all', class YnsReviewsAll extends HTMLElement {
      constructor() {
        super();
        this.firstChunk = parseInt(this.dataset.firstChunk, 10) || 24;
        this.chunkSize = parseInt(this.dataset.chunkSize, 10) || 60;
        this.total = parseInt(this.dataset.total, 10) || 0;
        this.sourceUrl = this.dataset.sourceUrl || '';
        this.cursor = 0;
        this.allReviews = [];
        this.visibleReviews = [];
        this.activeRating = null;
        this.sortMode = 'recent';
        this.chapterTemplates = [];
        this.consumedChapters = new Set();
        this.products = [];
        this.renderToken = 0;       // increments on _applyView to cancel stale rAF chains
      }

      async connectedCallback() {
        this.grid = this.querySelector('[data-yns-reviews-grid]');
        this.endCard = this.querySelector('[data-yns-end]');
        this.fallback = this.querySelector('[data-yns-fallback]');
        this.empty = this.querySelector('[data-yns-empty]');
        this.countNode = document.querySelector('[data-yns-reviews-count]');

        Array.from(this.querySelectorAll('template[data-yns-chapter]')).forEach((tmpl) => {
          this.chapterTemplates.push({
            after: parseInt(tmpl.dataset.afterCard, 10) || 0,
            html: tmpl.innerHTML
          });
        });
        this.chapterTemplates.sort((a, b) => a.after - b.after);

        // Load product pool — emitted server-side as a JSON script block
        var productsNode = this.querySelector('script[data-yns-products]');
        if (productsNode) {
          try {
            this.products = JSON.parse(productsNode.textContent || '[]');
            if (!Array.isArray(this.products)) this.products = [];
          } catch (err) { this.products = []; }
        }
        // Precompute keyword sets per product for fast matching
        this.products.forEach((p) => {
          p._keywords = (p.keywords || p.title || '')
            .toLowerCase()
            .split(/[^a-z0-9]+/)
            .filter(function (w) { return w.length >= 3; });
        });

        this._initFilters();

        try {
          var res = await fetch(this.sourceUrl, { credentials: 'omit' });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          this.allReviews = await res.json();
          if (!Array.isArray(this.allReviews)) throw new Error('Invalid JSON shape');
          this._removeSkeletons();
          this._applyView();
        } catch (err) {
          console.error('[yns-reviews] fetch failed', err);
          this._removeSkeletons();
          this._showFallback();
        }
      }

      _initFilters() {
        var root = this.closest('[data-yns-reviews-wall]') || this;
        this.chips = Array.from(root.querySelectorAll('[data-yns-filter]'));
        this.chips.forEach((chip) => {
          chip.addEventListener('click', (e) => {
            e.preventDefault();
            var value = parseInt(chip.getAttribute('data-yns-filter'), 10);
            var pressed = chip.getAttribute('aria-pressed') === 'true';
            if (pressed) {
              chip.setAttribute('aria-pressed', 'false');
              this.activeRating = null;
            } else {
              chip.setAttribute('aria-pressed', 'true');
              this.activeRating = value;
            }
            this._applyView();
          });
        });

        this.sortBtns = Array.from(root.querySelectorAll('[data-yns-sort]'));
        this.sortBtns.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.sortBtns.forEach((b) => b.setAttribute('aria-pressed', 'false'));
            btn.setAttribute('aria-pressed', 'true');
            this.sortMode = btn.getAttribute('data-yns-sort');
            this._applyView();
          });
        });

        var resets = root.querySelectorAll('[data-yns-filter-reset]');
        resets.forEach((el) => {
          el.addEventListener('click', (e) => {
            e.preventDefault();
            this.chips.forEach((c) => c.setAttribute('aria-pressed', 'false'));
            this.activeRating = null;
            this._applyView();
          });
        });
      }

      _applyView() {
        // Increment token so any in-flight rAF chain knows to abort
        var token = ++this.renderToken;

        var list = this.allReviews.slice();
        if (this.activeRating !== null) list = list.filter((r) => r.rating === this.activeRating);
        if (this.sortMode === 'rating') {
          list.sort((a, b) => (b.rating - a.rating) || (a.date < b.date ? 1 : -1));
        } else {
          list.sort((a, b) => (a.date < b.date ? 1 : -1));
        }
        this.visibleReviews = list;
        this.cursor = 0;
        this.consumedChapters = new Set();

        // Clear all rendered cards/chapters (leave skeletons alone — already removed)
        Array.from(this.grid.querySelectorAll('.yns-review-card:not([data-yns-skeleton]), .yns-review-chapter')).forEach((n) => {
          n.parentNode && n.parentNode.removeChild(n);
        });

        if (this.endCard) this.endCard.hidden = true;
        this._updateCount();

        if (!this.visibleReviews.length) {
          this._toggleEmpty();
          return;
        }
        this._toggleEmpty();

        // Render first chunk synchronously with stagger animation
        this._renderChunk(0, Math.min(this.firstChunk, this.visibleReviews.length), true, token);

        // Schedule remaining chunks via requestAnimationFrame
        this._scheduleRemaining(this.firstChunk, token);
      }

      _scheduleRemaining(start, token) {
        if (token !== this.renderToken) return;        // view changed; abort
        if (start >= this.visibleReviews.length) {
          if (this.endCard) this.endCard.hidden = false;
          return;
        }
        var next = Math.min(start + this.chunkSize, this.visibleReviews.length);
        requestAnimationFrame(() => {
          if (token !== this.renderToken) return;
          this._renderChunk(start, next, false, token);
          this._scheduleRemaining(next, token);
        });
      }

      _renderChunk(start, end, animate, token) {
        if (token !== this.renderToken) return;
        var fragment = document.createDocumentFragment();
        var batchCards = [];

        for (var i = start; i < end; i++) {
          this._maybeInsertChapter(fragment, i);
          var card = this._buildCard(this.visibleReviews[i], i);
          if (card) {
            fragment.appendChild(card);
            batchCards.push(card);
          }
        }
        var insertBefore = this.empty || null;
        this.grid.insertBefore(fragment, insertBefore);
        this.cursor = end;

        if (animate) this._queueEntryAnimation(batchCards);
        this._updateCount();
      }

      _maybeInsertChapter(fragment, cardIndex) {
        for (var i = 0; i < this.chapterTemplates.length; i++) {
          var ch = this.chapterTemplates[i];
          var key = i + ':' + ch.after;
          if (this.consumedChapters.has(key)) continue;
          if (ch.after === cardIndex && cardIndex > 0) {
            var wrap = document.createElement('div');
            wrap.innerHTML = ch.html.trim();
            var node = wrap.firstChild;
            while (node && node.nodeType !== 1) node = node.nextSibling;
            if (node) fragment.appendChild(node);
            this.consumedChapters.add(key);
          }
        }
      }

      _updateCount() {
        if (!this.countNode) return;
        // Always show the number of reviews in the current view (filtered or full)
        this.countNode.textContent = this.visibleReviews.length.toLocaleString('en-US');
      }

      _toggleEmpty() {
        if (!this.empty) return;
        this.empty.hidden = this.visibleReviews.length > 0;
      }

      _removeSkeletons() {
        this.grid.querySelectorAll('[data-yns-skeleton]').forEach((s) => s.remove());
        this.grid.setAttribute('data-state', 'hydrated');
      }

      _showFallback() {
        if (this.fallback) this.fallback.hidden = false;
      }

      _queueEntryAnimation(cards) {
        if (prefersReducedMotion) return;
        cards.forEach((card, i) => {
          card.style.setProperty('--yns-anim-index', Math.min(i, 12));
          void card.offsetWidth;
          card.setAttribute('data-anim', 'enter');
        });
      }

      // -------- Per-card product picker --------
      // V4: prefer the pre-baked rev.product field (produced offline by
      // tmp/yns/match-products.py). Fall back to runtime keyword scoring +
      // hash for any reviews lacking the field (e.g., manually-added entries).
      _pickProduct(rev, index) {
        if (!this.products.length) return null;

        if (rev.product) {
          for (var i = 0; i < this.products.length; i++) {
            if (this.products[i].handle === rev.product) return this.products[i];
          }
          // Handle not in current pool (admin removed it) — fall through to legacy logic
        }
        return this._pickByKeyword(rev) || this._pickByHash(rev, index);
      }

      _pickByKeyword(rev) {
        var msg = (rev.message || '').toLowerCase();
        if (!msg) return null;
        var best = null;
        var bestScore = 0;
        for (var pi = 0; pi < this.products.length; pi++) {
          var p = this.products[pi];
          var score = 0;
          for (var ki = 0; ki < p._keywords.length; ki++) {
            if (msg.indexOf(p._keywords[ki]) !== -1) score++;
          }
          if (score > bestScore) { bestScore = score; best = p; }
        }
        return bestScore >= 1 ? best : null;
      }

      _pickByHash(rev, index) {
        var key = (rev.reviewer || '') + '|' + (rev.date || '') + '|' + index;
        return this.products[stableHash(key) % this.products.length];
      }

      _buildCard(rev, index) {
        if (!rev) return null;

        var rating = rev.rating || 5;
        var reviewer = rev.reviewer || 'Verified customer';
        var message = (rev.message || '').trim();
        var dateLabel = this._formatDate(rev.date);

        var card = document.createElement('article');
        card.className = 'yns-review-card';
        card.setAttribute('role', 'listitem');
        card.setAttribute('data-rating', String(rating));

        var head = document.createElement('div');
        head.className = 'yns-review-card__head';
        var avatar = document.createElement('span');
        avatar.className = 'yns-review-card__avatar';
        avatar.setAttribute('aria-hidden', 'true');
        avatar.textContent = reviewer.trim().charAt(0).toUpperCase() || '★';
        head.appendChild(avatar);
        var meta = document.createElement('div');
        meta.className = 'yns-review-card__head-meta';
        var name = document.createElement('div');
        name.className = 'yns-review-card__name';
        name.textContent = reviewer;
        meta.appendChild(name);
        if (dateLabel) {
          var role = document.createElement('div');
          role.className = 'yns-review-card__role';
          role.textContent = dateLabel;
          meta.appendChild(role);
        }
        head.appendChild(meta);
        var verifiedEl = document.createElement('span');
        verifiedEl.className = 'yns-review-card__verified';
        verifiedEl.innerHTML = '<span class="yns-sticker yns-sticker--sky yns-sticker--sm">VERIFIED</span>';
        head.appendChild(verifiedEl);
        card.appendChild(head);

        var stars = document.createElement('div');
        stars.className = 'yns-review-card__stars';
        stars.setAttribute('aria-label', rating + ' out of 5 stars');
        var starStr = '';
        for (var i = 0; i < 5; i++) starStr += i < rating ? '★' : '☆';
        stars.textContent = starStr;
        card.appendChild(stars);

        var quote = document.createElement('blockquote');
        quote.className = 'yns-review-card__quote';
        if (message) {
          quote.textContent = message;
        } else {
          quote.className += ' yns-review-card__quote--empty';
          quote.textContent = 'Rated ' + rating + ' stars · No written review';
        }
        card.appendChild(quote);

        var footer = document.createElement('footer');
        footer.className = 'yns-review-card__footer';
        var product = this._pickProduct(rev, index);
        if (product && product.url && product.title) {
          var pLink = document.createElement('a');
          pLink.className = 'yns-review-card__product-link';
          pLink.href = product.url;
          pLink.setAttribute('aria-label', 'View ' + product.title);
          var label = document.createElement('span');
          label.className = 'yns-review-card__product-link__label';
          label.textContent = 'Bought';
          var title = document.createElement('span');
          title.className = 'yns-review-card__product-link__title';
          title.textContent = product.title.length > 48 ? product.title.slice(0, 47) + '…' : product.title;
          var arrow = document.createElement('span');
          arrow.className = 'yns-review-card__product-link__arrow';
          arrow.setAttribute('aria-hidden', 'true');
          arrow.textContent = '→';
          pLink.appendChild(label);
          pLink.appendChild(title);
          pLink.appendChild(arrow);
          footer.appendChild(pLink);
        }
        card.appendChild(footer);

        return card;
      }

      _formatDate(iso) {
        if (!iso) return '';
        var parts = iso.split('-');
        if (parts.length !== 3) return '';
        var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        var m = parseInt(parts[1], 10) - 1;
        if (m < 0 || m > 11) return '';
        return months[m] + ' ' + parts[0];
      }
    });
  }

  // -----------------------------------------------------------
  // Sticky CTA
  // -----------------------------------------------------------
  function initStickyCta(root) {
    var dismissKey = 'yns-reviews-cta-dismissed';
    if (sessionStorage.getItem(dismissKey) === '1') {
      root.remove();
      return;
    }

    var engagementMs = parseInt(root.getAttribute('data-engagement-ms') || '3000', 10);
    var scrolledEnough = false;
    var timer = null;

    function show() { root.setAttribute('data-state', 'visible'); }

    function onScroll() {
      if (scrolledEnough) return;
      if (window.scrollY > 600) {
        scrolledEnough = true;
        if (!timer) timer = setTimeout(show, engagementMs);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    var closeBtn = root.querySelector('[data-yns-cta-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        root.removeAttribute('data-state');
        sessionStorage.setItem(dismissKey, '1');
        if (timer) { clearTimeout(timer); timer = null; }
        window.removeEventListener('scroll', onScroll);
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-yns-stat-hero]').forEach(initStatHero);
    document.querySelectorAll('[data-yns-reviews-cta]').forEach(initStickyCta);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
